import moment from 'moment';
import scenarioStylesheet from '@/utils/scenarioCSS';

const refRegex = /\[|\]/gi;

const NISTDisclaimer = `
  <p>This attachment contains data sourced from NIST SP 800-53 Revision 5. See links below
      for additional terms and conditions, trademarks, copyrights, and intellectual restrictions that
      apply upon use of this application.</p>
  <p><a
    href="https://csrc.nist.gov/publications/detail/sp/800-53/rev-5/final"
    target="__blank">
    NIST SP 800-53 Revision 5
  </a></p>
  <p><a
    href="https://www.nist.gov/topics/data/public-access-nist-research/copyright-fair-use-and-licensing-statements-srd-data-and"
    target="__blank">
    NIST SP 800-53 Fair Use
  </a></p>
`;

function setPlayHTML(play) {
  let html = `<div>
    <h3 class="playTitle">${play.id} - ${play.name}</h3>`;
  html += `<p>${play.description}</p>`;
  const capecs = play.attackpatterns.map((capec) => `CAPEC-${capec.id}`);
  html += `<p><span class="subtitle">CAPEC™ Attack Patterns:</span>${capecs.length > 0 ? capecs.join(', ') : '--'}</p>`;
  const teqs = play.techniques.map((tq) => tq.mitre_id);
  html += `<p><span class="subtitle">MITRE ATT&CK Techniques:</span>${teqs.length > 0 ? teqs.join(', ') : '--'}</p>`;
  const ctrls = play.controls.map((ctrl) => ctrl.label);
  html += `<p><span class="subtitle">NIST RMF 800-53 Controls:</span>${ctrls.length > 0 ? ctrls.join(', ') : '--'}</p>`;
  const cwes = play.cwes.map((cwe) => `CWE-${cwe.id}`);
  html += `<p><span class="subtitle">CWE™ Weaknesses:</span>${cwes.length > 0 ? cwes.join(', ') : '--'}</p>`;
  html += '<p><span class="subtitle">Tools:</span></p>';
  const tools = play.tools.map((tool) => `${tool.name} - ${tool.url}`);
  html += `<p class="tool-list">${tools.length > 0 ? tools.join('</p><p class="tool-list">') : '--'}</p>`;
  const tags = play.tags.map((tag) => tag.name);
  html += `<p><span class="subtitle">Tags:</span>${tags.length > 0 ? tags.join(', ') : '--'}</p>`;
  const objectives = play.objectives.map((objctv) => objctv.title);
  html += `<p><span class="subtitle">Penetration Testing Objectives:</span>${objectives.length > 0 ? objectives.join(', ') : '--'}</p>`;
  html += '</div><br />';
  return html;
}

function setCAPECAppendixHTML(pattern) {
  let html = `<div>
    <h3 class="playTitle">CAPEC-${pattern.id} ${pattern.name} (${pattern.status})</h3>`;
  html += `<p>${pattern.description}</p>`;
  if (pattern.parent !== null) html += `<p><span class="subtitle">Parent: </span>CAPEC-${pattern.parent}</p>`;
  if (pattern.next_related !== null) html += `<p><span class="subtitle">Precedes: </span>CAPEC-${pattern.next_related}</p>`;
  html += '<p><span class="subtitle">Likelihood: </span>';
  html += pattern.likelihood === '' ? '--</p>' : `${pattern.likelihood}</p>`;
  html += '<p><span class="subtitle">Severity: </span>';
  html += pattern.severity === '' ? '--</p>' : `${pattern.severity}</p>`;
  if (pattern.related_weaknesses.length > 0) {
    html += '<p><span class="subtitle">Related CWE™ Weaknesses: </span>';
    const sublist = pattern.related_weaknesses.map((cwe) => `CWE-${cwe}`);
    html += sublist.join(', ');
  } else {
    html += '<p><span class="subtitle">Related CWE™ Weaknesses:</span>--';
  }
  html += '</p></div><br />';
  return html;
}

function setTeqAppendixHTML(teq) {
  let html = `<div>
    <h3 class="playTitle">${teq.mitre_id} - ${teq.name}`;
  if (teq.revoked) html += '(Revoked) ';
  if (teq.deprecated) html += '(Deprecated) ';
  html += '</h3>';
  html += `<p>${teq.description}</p>`;
  let tactics = teq.tactics.map((tac) => `${tac.mitre_id} - ${tac.name}`);
  tactics = tactics.join(', ');
  html += `<p><span class="subtitle">Tactics:</span>${tactics}</p>`;
  const mitigations = teq.mitigations.map((pltfm) => pltfm.name);
  html += `<p><span class="subtitle">Mitigations:</span>${mitigations.length > 0 ? mitigations.join(', ') : '--'}</p>`;
  const platforms = teq.platforms.map((pltfm) => pltfm.name);
  html += `<p><span class="subtitle">Platforms:</span>${platforms.length > 0 ? platforms.join(', ') : '--'}</p>`;
  const software = teq.software_procedures.map((sftw) => sftw.software);
  html += `<p><span class="subtitle">Software:</span>${software.length > 0 ? software.join(', ') : '--'}</p>`;
  const groups = teq.group_procedures.map((grp) => grp.group);
  html += `<p><span class="subtitle">Groups:</span>${groups.length > 0 ? groups.join(', ') : '--'}</p>`;
  html += '</div><br />';
  return html;
}

function setCtrlAppendixHTML(control) {
  let html = `<div>
    <h3 class="playTitle">${control.label} - ${control.title}</h3>`;
  html += `<p><span class="subtitle">Family:</span>${control.family.name}</p>`;
  html += `<p><span class="subtitle">Control:</span>${control.statement}</p>`;
  html += `<p><span class="subtitle">Discussion:</span>${control.guidance}</p>`;
  let related = control.related.split(',');
  if (related.length === 1 && related[0] === '') related = [];
  related = related.length > 0 ? related.join(', ') : '--';
  html += `<p><span class="subtitle">Related Controls:</span>${related}</p>`;
  let refs = control.references;
  refs = refs.replace(refRegex, '');
  refs = refs.split(',');
  if (refs.length === 1 && refs[0] === '') refs = [];
  refs = refs.length > 0 ? refs.join(', ') : '--';
  html += `<p><span class="subtitle">References:</span>${refs}</p>`;

  if (control.enhancements.length > 0) {
    html += '<p><span class="subtitle">Enhancements:</span></p>';
    const sublist = control.enhancements.map((sub) => `<span>${sub.label} ${sub.title}</span>`);
    html += sublist.join(', ');
  } else {
    html += '<p><span class="subtitle">Enhancements:</span>--</p>';
  }
  html += '</div><br />';
  return html;
}

function setCWEAppendixHTML(weakness) {
  let html = `<div>
    <h3 class="playTitle">CWE-${weakness.id} ${weakness.name} (${weakness.status})</h3>`;
  if (weakness.parent !== null) html += `<p><span class="subtitle">Parent: </span>CWE-${weakness.parent}</p>`;
  if (weakness.next_related !== null) html += `<p><span class="subtitle">Precedes: </span>CWE-${weakness.next_related}</p>`;
  html += '<p><span class="subtitle">Weakness Abstraction: </span>';
  html += weakness.weakness_abstraction === '' ? '--</p>' : `${weakness.weakness_abstraction}</p>`;
  html += `<p>${weakness.description}</p>`;
  html += `<p>${weakness.ext_description}</p>`;
  if (weakness.related_attack_patterns.length > 0) {
    html += '<p><span class="subtitle">Related CAPEC™ Attack Patterns: </span>';
    const sublist = weakness.related_attack_patterns.map((cwe) => `CAPEC-${cwe}`);
    html += sublist.join(', ');
  } else {
    html += '<p><span class="subtitle">Related CAPEC™ Attack Patterns:</span>--';
  }
  html += '</div><br />';
  return html;
}

export function exportScenarioToPDF(
  scenarioBuilder,
  capec,
  mitre,
  nist,
  cwe
) {
  const {
    classification,
    name,
    description,
    plays
  } = scenarioBuilder;
  const [capecInclude, capecList, capecAppendix] = capec;
  const [mitreInclude, mitreList, mitreAppendix] = mitre;
  const [nistInclude, nistList, nistAppendix] = nist;
  const [cweInclude, cweList, cweAppendix] = cwe;
  const title = name.length > 0 ? name : 'Playbook Excerpt';
  const hasDesc = description.length > 0;
  const playList = plays.map((play) => `Play #${play.id}`);

  const preHtml = `<html><head><meta charset='utf-8'>
    <title>Playbook Excerpt</title>
    <style>${scenarioStylesheet}</style>
  </head><body>`;
  let html = preHtml;
  html += `<div class="classification header">${classification.toUpperCase()}</div>
  <div class="classification footer"><div class="bottom">${classification.toUpperCase()}</div></div>
  <table>
    <thead><tr><td>
      <div class="header-space">&nbsp;</div>
    </td></tr></thead>
    <tbody>
      <tr><td>
        <div class="CoverPage">`;
  html += `<div class="docHeader">
      <div class="charcoalBox"><h1 class="rptTitle">${title}</h1></div>
      <div class="greenBox"></div>
    </div>
    <div class="timestamp">${moment().format('DD MMMM YYYY')}</div>
    <div class="contents">
      <ul>`;
  if (hasDesc) {
    html += `<li>Description</li><p class="coverDetails">${description}</p>`;
  }
  html += `<li>Playbook Excerpt</li><p class="coverDetails">${playList.join(', ')}</p>`;
  if (capecInclude) html += `<li>Attack Patterns Appendix</li><p class="coverDetails">${capecList.length > 0 ? capecList.join(', ') : '--'}</p>`;
  if (mitreInclude) html += `<li>Techniques Appendix</li><p class="coverDetails">${mitreList.length > 0 ? mitreList.join(', ') : '--'}</p>`;
  if (nistInclude) html += `<li>Controls Appendix</li><p class="coverDetails">${nistList.length > 0 ? nistList.join(', ') : '--'}</p>`;
  if (cweInclude) html += `<li>Weaknesses Appendix</li><p class="coverDetails">${cweList.length > 0 ? cweList.join(', ') : '--'}</p>`;
  html += `</ul></div>
    </div>`;
  html += `</div>
    <div class="ScenarioBuilder">
      <h3 class="newPage sectionTitle green">Plays</h3>`;
  const playsHTML = plays.map((play) => setPlayHTML(play));
  html += playsHTML.join('');

  if (capecInclude) {
    html += '<h3 class="newPage sectionTitle green">CAPEC™ Attack Patterns Appendix</h3>';
    html += `<p>This attachment contains data sourced from the Common Attack Pattern Enumeration and
    Classification (CAPEC™) effort. See links below for additional terms and conditions, trademarks,
    copyrights, and intellectual restrictions that apply upon use of this application.</p>`;
    html += `<p><a
      href="https://capec.mitre.org/about/termsofuse.html"
      target="__blank">
      CAPEC™ Terms of Use
    </a></p>`;
    if (capecList.length > 0) {
      const capecAppendixHTML = capecList.map((mpg) => {
        const attackpattern = capecAppendix.find((capec) => mpg === `CAPEC-${capec.id}`);
        return setCAPECAppendixHTML(attackpattern);
      });
      html += capecAppendixHTML.join('');
    } else html += '<p>No attack patterns mapped to selected plays</p>';
  }

  if (mitreInclude) {
    html += '<h3 class="newPage sectionTitle green">MITRE ATT&CK Techniques Appendix</h3>';
    html += `<p>This application contains data sourced from MITRE's ATT&amp;CK&reg; framework as well
    as data from NIST SP 800-53. See links below for additional terms and conditions, trademarks,
    copyrights, and intellectual restrictions that apply upon use of this application.</p>`;
    html += `<p><a
      href="https://attack.mitre.org/resources/terms-of-use/"
      target="__blank">
      MITRE ATT&amp;CK&reg; Terms of Use
    </a></p>`;
    if (mitreList.length > 0) {
      const teqAppendixHTML = mitreList.map((mpg) => {
        const technique = mitreAppendix.find((teq) => mpg === teq.mitre_id);
        return setTeqAppendixHTML(technique);
      });
      html += teqAppendixHTML.join('');
    } else html += '<p>No techniques mapped to selected plays</p>';
  }

  if (nistInclude) {
    html += '<h3 class="newPage sectionTitle green">NIST SP 800-53 Appendix</h3>';
    html += NISTDisclaimer;
    if (nistList.length > 0) {
      const ctrlAppendixHTML = nistList.map((mpg) => {
        const control = nistAppendix.find((ctrl) => mpg === ctrl.label);
        return setCtrlAppendixHTML(control);
      });
      html += ctrlAppendixHTML.join('');
    } else html += '<p>No controls mapped to selected plays</p>';
  }

  if (cweInclude) {
    html += '<h3 class="newPage sectionTitle green">CWE™ Weaknesses Appendix</h3>';
    html += `<p>This attachment contains data sourced from Common Weakness Enumeration (CWE™). See
    links below for additional terms and conditions, trademarks, copyrights, and intellectual
    restrictions that apply upon use of this application.</p>`;
    html += `<p><a
      href="https://cwe.mitre.org/about/termsofuse.html"
      target="__blank">
      CWE Terms of Use
    </a></p>`;
    if (cweList.length > 0) {
      const cweAppendixHTML = cweList.map((mpg) => {
        const weakness = cweAppendix.find((cwe) => mpg === `CWE-${cwe.id}`);
        return setCWEAppendixHTML(weakness);
      });
      html += cweAppendixHTML.join('');
    } else html += '<p>No weaknesses mapped to selected plays</p>';
  }

  html += `</div></td></tr>
    </tbody>
    <tfoot><tr><td>
      <div class="footer-space">&nbsp;</div>
    </td></tr></tfoot>
  </table>
  </body></html>`;
  return html;
}

export function exportScenarioToCSV(
  scenarioBuilder,
  capec,
  mitre,
  nist,
  cwe
) {
  const [capecInclude, capecAppendix] = capec;
  const [mitreInclude, mitreAppendix] = mitre;
  const [nistInclude, nistAppendix] = nist;
  const [cweInclude, cweAppendix] = cwe;
  let capecCSV;
  let teqCSV;
  let ctrlCSV;
  let cweCSV;

  const playHeader = ['Play ID', 'Play Name', 'Description', 'CAPEC Attack Patterns', 'MITRE ATT&CK Techniques', 'NIST SP 800-53 Rev.5 Controls', 'CWE Weaknesses', 'Tools', 'Tags', 'Objectives'];
  const playContent = scenarioBuilder.plays.map((play) => ([
    play.id,
    `"${play.name}"`,
    `"${play.description}"`,
    play.attackpatterns.map((capec) => `CAPEC-${capec.id}`).join('; '),
    play.techniques.map((teq) => teq.mitre_id).join('; '),
    play.controls.map((ctrl) => ctrl.label).join('; '),
    play.cwes.map((cwe) => `CWE-${cwe.id}`).join('; '),
    play.tools.map((tool) => tool.name).join('; '),
    play.tags.map((tag) => tag.name).join('; '),
    play.objectives.map((objctv) => objctv.title).join('; ')
  ]));
  const playRows = [playHeader, ...playContent];
  const playCSV = `data:text/csv;charset=utf-8,${playRows.map((e) => e.join(',')).join('\n')}`;

  if (capecInclude) {
    const capecHeader = ['CAPEC ID', 'Name', 'Description', 'Abstraction', 'Status', 'Alternate Terms', 'Likelihood', 'Severity', 'Parent', 'Precedes', 'Related MITRE ATT&CK Techniques', 'Related CWE Weaknesses', 'Related Plays'];
    const capecContent = capecAppendix.map((capec) => ([
      `CAPEC-${capec.id}`,
      `"${capec.name}"`,
      `"${capec.description}"`,
      capec.abstraction,
      capec.status,
      capec.alternate_terms.flatMap((a) => a.map((b) => b[1])).join('; '),
      capec.likelihood,
      capec.severity,
      capec.parent === null ? '' : `CAPEC-${capec.parent}`,
      capec.next_related === null ? '' : `CAPEC-${capec.next_related}`,
      capec.taxonomy_mappings.map((a) => a[1][1]).join('; '),
      capec.related_weaknesses.map((cwe) => `CWE-${cwe}`).join('; '),
      capec.related_plays.map((ply) => ply.id).join('; ')
    ]));
    const capecRows = [capecHeader, ...capecContent];
    capecCSV = `data:text/csv;charset=utf-8,${capecRows.map((e) => e.join(',')).join('\n')}`;
  } else capecCSV = '';

  if (mitreInclude) {
    const teqHeader = ['MITRE ID', 'Technique Name', 'Description', 'MITRE Matrix', 'Revoked', 'Deprecated', 'MITRE ATT&CK Sub-Techniques', 'MITRE ATT&CK Tactics', 'Mitigations', 'Platforms', 'Software Procedures', 'Group Procedures', 'Related NIST SP 800-53 Rev.5 Controls', 'Related Plays'];
    const teqContent = mitreAppendix.map((teq) => ([
        teq.mitre_id,
        `"${teq.name}"`,
        `"${teq.description}"`,
        teq.matrix_type,
        teq.revoked,
        teq.deprecated,
        teq.subtechniques.map((sub) => sub.mitre_id).join('; '),
        teq.tactics.map((tac) => `${tac.mitre_id} - ${tac.name}`).join('; '),
        teq.mitigations.map((mit) => mit.name).join('; '),
        teq.platforms.map((pltf) => pltf.name).join('; '),
        teq.software_procedures.join('; '),
        teq.group_procedures.join('; '),
        teq.related_controls.map((ctrl) => ctrl.label).join('; '),
        teq.related_plays.map((ply) => ply.id).join('; ')
      ]));
    const teqRows = [teqHeader, ...teqContent];
    teqCSV = `data:text/csv;charset=utf-8,${teqRows.map((e) => e.join(',')).join('\n')}`;
  } else teqCSV = '';

  if (nistInclude) {
    const ctrlHeader = ['NIST Control', 'Title', 'Family', 'Source URL', 'Control', 'Discussion', 'Related Controls', 'References', 'Enhancements', 'Related Plays'];
    const ctrlContent = nistAppendix.map((ctrl) => {
      let related = ctrl.related.split(',');
      if (related.length === 1 && related[0] === '') related = [];
      let refs = ctrl.references;
      refs = refs.replace(refRegex, '');
      refs = refs.split(',');
      if (refs.length === 1 && refs[0] === '') refs = [];
      return [
        ctrl.label,
        `"${ctrl.title}"`,
        `"${ctrl.family.name}"`,
        ctrl.url,
        `"${ctrl.statement.replace(/\r?\n|\r|\t/g, ' ').replace(/#/g, '')}"`,
        `"${ctrl.guidance.replace(/\r?\n|\r|\t/g, ' ').replace(/#/g, '')}"`,
        related.join('; '),
        refs.join('; '),
        ctrl.enhancements.map((enh) => enh.label).join('; '),
        ctrl.related_plays.map((ply) => ply.id).join('; ')
      ];
    });
    const ctrlRows = [ctrlHeader, ...ctrlContent];
    ctrlCSV = `data:text/csv;charset=utf-8,${ctrlRows.map((e) => e.join(',')).join('\n')}`;
  } else ctrlCSV = '';

  if (cweInclude) {
    const cweHeader = ['CWE ID', 'Name', 'Description', 'Extended Description', 'Weakness Abstraction', 'Status', 'Parent', 'Next Related Weakness', 'Related Attack Patterns', 'Related Plays'];
    const cweContent = cweAppendix.map((cwe) => ([
      `CWE-${cwe.id}`,
      `"${cwe.name}"`,
      `"${cwe.description}"`,
      `"${cwe.ext_description}"`,
      `"${cwe.weakness_abstraction}"`,
      `"${cwe.status}"`,
      cwe.parent === null ? '' : `CWE-${cwe.parent}`,
      cwe.next_related === null ? '' : `CWE-${cwe.next_related}`,
      cwe.related_attack_patterns.map((ap) => `CAPEC-${ap.id}`).join('; '),
      cwe.related_plays.map((ply) => ply.id).join('; ')
    ]));
    const cweRows = [cweHeader, ...cweContent];
    cweCSV = `data:text/csv;charset=utf-8,${cweRows.map((e) => e.join(',')).join('\n')}`;
  } else cweCSV = '';

  return {
    playCSV,
    capecCSV,
    teqCSV,
    ctrlCSV,
    cweCSV
  };
}

export function exportRMFToPDF(control) {
  const preHtml = `<html><head><meta charset='utf-8'>
    <title>PTaaS Share an RMF Control: ${control.label}</title>
    <style>${scenarioStylesheet}</style>
  </head>`;
  let html = preHtml;
  html += '<body><div>';
  html += setCtrlAppendixHTML(control);
  html += '<h3 class="newPage sectionTitle green">NIST SP 800-53 Rev. 5</h3>';
  html += NISTDisclaimer;
  html += '</div></body></html>';
  return html;
}
